import type { Actor } from '../../sim/actor';
import type { HitResult } from '../../sim/combat';
import type { ClassDef, IdentityController } from '../../sim/registry';
import { startCast } from '../../sim/skillRuntime';
import { T, tripod } from '../../sim/skills';
import type { HitSpec, ResolvedSkill, SkillAction, SkillDef, SkillStage } from '../../sim/skillTypes';
import { registerStatus } from '../../sim/status';
import type { Reaction } from '../../sim/types';
import type { World } from '../../sim/world';
import { classBaseStats } from '../balance';
import { area, basicAttack, circle, cone, dashSkill, hit, hits, identityOp, invuln, move, rect, ring, self, skill, stage } from './kit';

/**
 * WINDFIST — gale-monk brawler (generator → orb-spender martial artist).
 * Generator kicks build Chi (3 orbs of 100); Chi Arts spend orbs instead of
 * mana. Spending 12 orbs unlocks Surge of Chi (Z). Still Water (X) is a parry
 * stance: a blow that lands during it is negated and answered with a
 * counter-strike.
 */

const HUE = 0x8fffe0;
const ORB_CHI = 100;
const MAX_ORBS = 3;
/** Orbs that must be spent before Surge of Chi becomes available. */
const SURGE_COST = 12;
const SURGE_BONUS = 0.4;
const PARRY_WINDOW = 0.6;
/** Still Water's remaining cooldown after a stance that caught nothing. */
const PARRY_REFUND_CD = 5;
/** Identity data key paid by Chi Arts through `resourceCost`. */
const ORBS = 'orbs';
const STANCE = 'wf_still_water';
const RIPOSTE = 'wf_riposte';
/** Chi Arts in a fixed order: `data.empowered` stores index + 1 of the one Surge empowered. */
const CHI_ARTS = ['wf_palm', 'wf_tiger', 'wf_crane_kick', 'wf_cyclone'];

const FLINCH: Reaction = { kind: 'flinch' };

registerStatus({ id: 'wf_tailwind', name: 'Tailwind Step', kind: 'buff', icon: 'wf_tailwind', description: 'Attack and move speed +10%.', stacking: 'refresh', mods: { atkSpeedPct: 0.1, moveSpeedPct: 0.1 }, color: HUE });
registerStatus({ id: 'wf_frostbite', name: 'Frostbitten', kind: 'debuff', icon: 'wf_frostbite', description: 'Move speed -30%.', stacking: 'refresh', mods: { moveSpeedPct: -0.3 }, element: 'water', cleansable: true, color: 0x9fe8ff });
// One-shot lightning discharge (Lightning Storm facet): a single damage tick shortly after it lands.
registerStatus({ id: 'wf_arc', name: 'Arc Discharge', kind: 'debuff', icon: 'wf_arc', description: 'Struck by a chi discharge.', stacking: 'refresh', tick: { interval: 0.05, kind: 'damage' }, element: 'lightning', hidden: true, color: 0x9ab8ff });

// ------------------------------------------------------------------ skills

const thunderKicks = skill({
  id: 'wf_thunder_kicks', name: 'Rolling Thunder Kicks', hue: HUE,
  description: 'GENERATOR (+80 Chi). Three lightning-fast kicks, then a stomp that jolts foes off their feet.',
  castType: 'normal', cooldown: 9, mana: 55, positional: 'back', superArmor: 'paralysis', weakPoint: 1,
  identityGain: 80, tags: ['generator'],
  stages: [
    stage('Kick', 0.9, [
      hit(0.1, cone(3.5, 90), 0.7, { reaction: FLINCH, weakPoint: 0 }),
      hit(0.24, cone(3.5, 90), 0.7, { reaction: FLINCH, weakPoint: 0 }),
      hit(0.38, cone(3.5, 90), 0.7, { reaction: FLINCH, weakPoint: 0 }),
      hit(0.58, cone(3.5, 90), 1.7, { reaction: { kind: 'launch', power: 0.2, duration: 0.6 }, vfx: 'heavy' }),
    ]),
  ],
});

const risingCrane = skill({
  id: 'wf_rising_crane', name: 'Rising Crane', hue: HUE,
  description: 'GENERATOR (+30 Chi per strike). Chain up to three strikes: a roundhouse, a launching knee and a rolling slam that carries you 4 m. Each follow-up adds 2 s to the cooldown.',
  castType: 'chain', cooldown: 8, mana: 50, positional: 'back', superArmor: 'paralysis', followWindow: 2, chainPenalty: 2,
  identityGain: 90, tags: ['generator'],
  stages: [
    stage('Kick', 0.45, [hit(0.15, cone(3, 160), 1.3, { reaction: FLINCH })]),
    stage('Attack2', 0.5, [move(0, 1, 0.12), hit(0.16, cone(3, 100), 1.3, { reaction: { kind: 'launch', power: 0.3, duration: 0.8 } })]),
    stage('Leap', 0.65, [move(0, 4, 0.3), hit(0.34, circle(2.4, 0.6), 2.2, { reaction: { kind: 'knockdown', duration: 1, power: 0.5 }, vfx: 'heavy' })]),
  ],
});

const crescentSweep = skill({
  id: 'wf_crescent_sweep', name: 'Crescent Sweep', hue: HUE,
  description: 'GENERATOR (+60 Chi). A sweeping roundhouse, then a rising kick you can re-aim. Counters blue-glowing enemies.',
  castType: 'combo', cooldown: 8, mana: 50, positional: 'back', counter: true, followWindow: 0.8,
  identityGain: 60, tags: ['generator'],
  stages: [
    stage('Kick', 0.42, [hit(0.14, circle(3), 1.2, { reaction: FLINCH })]),
    stage('Attack3', 0.5, [hit(0.18, cone(3.2, 100), 1.8, { reaction: { kind: 'launch', power: 0.3, duration: 0.8 } })]),
  ],
});

const galeWalk = skill({
  id: 'wf_gale_walk', name: 'Gale Walk', hue: HUE,
  description: 'GENERATOR (+120 Chi). Become a whirlwind of kicks for 2.5 s, striking everything around you 20 times while you steer (up to ~14 m).',
  castType: 'normal', cooldown: 20, mana: 90, positional: 'back', superArmor: 'paralysis', stagger: 'midHigh',
  identityGain: 120, tags: ['generator'],
  stages: [stage('Spin', 2.5, [hits(0.1, 20, 0.12, circle(2.5), 9, { reaction: FLINCH })], { moveMult: 1.1, turnRate: 8 })],
});

const palmOfHeaven = skill({
  id: 'wf_palm', name: 'Palm of Heaven', hue: HUE, icon: 'fist',
  description: 'CHI ART (1 orb). A thunderous palm that blasts foes away. Counters blue-glowing enemies.',
  castType: 'normal', cooldown: 8, mana: 0, positional: 'back', counter: true, stagger: 'mid', weakPoint: 1,
  resourceCost: { kind: ORBS, amount: 1 }, tags: ['chiArt'],
  stages: [stage('Attack2', 0.55, [hit(0.2, cone(4, 60), 7, { reaction: { kind: 'knockback', power: 3 }, vfx: 'heavy' })])],
});

const tigerAscends = skill({
  id: 'wf_tiger', name: 'Tiger Ascends', hue: HUE, icon: 'claw',
  description: 'CHI ART (2 orbs). Dash 4 m, strike twice, then drive three rising blows that hurl foes skyward.',
  castType: 'normal', cooldown: 18, mana: 0, positional: 'back', superArmor: 'paralysis', stagger: 'high', weakPoint: 1,
  resourceCost: { kind: ORBS, amount: 2 }, tags: ['chiArt'],
  stages: [
    stage('Attack3', 1.15, [
      move(0, 4, 0.2),
      hit(0.22, cone(3, 120), 2, { reaction: FLINCH, stagger: 'low' }),
      hit(0.36, cone(3, 120), 2, { reaction: FLINCH, stagger: 'none', weakPoint: 0 }),
      hits(0.55, 3, 0.15, circle(3), 11, { reaction: { kind: 'launch', power: 0.4, duration: 1 }, weakPoint: 0 }),
    ]),
  ],
});

const thunderCraneKick = skill({
  id: 'wf_crane_kick', name: 'Thunder Crane Kick', hue: HUE,
  description: 'CHI ART (2 orbs). A towering crane kick that splits the ground in a 5 m line and launches foes.',
  castType: 'normal', cooldown: 18, mana: 0, positional: 'back', superArmor: 'paralysis', stagger: 'high', weakPoint: 2,
  resourceCost: { kind: ORBS, amount: 2 }, tags: ['chiArt'],
  stages: [stage('Kick', 0.75, [hit(0.3, rect(5, 2.5), 16, { reaction: { kind: 'launch', power: 1, duration: 1 }, vfx: 'heavy' })])],
});

const cycloneForm = skill({
  id: 'wf_cyclone', name: 'Cyclone Form', hue: HUE,
  description: 'CHI ART (1 orb). Hold to become a roaming tornado for up to 3 s, dragging in and battering everything around you 13 times.',
  castType: 'holding', cooldown: 16, mana: 0, positional: 'back', superArmor: 'paralysis', stagger: 'high',
  resourceCost: { kind: ORBS, amount: 1 }, tags: ['chiArt'],
  stages: [],
  // Per-tick stagger is kept low so 13 ticks land near the skill's High tier in total.
  holding: { maxHold: 3, tickInterval: 3 / 13, anim: 'Spin', moveMult: 1, tick: [hit(0, circle(3.5), 0.9, { reaction: { kind: 'pull', power: 0.6 }, stagger: 'low' })] },
});

const hundredHeavens = skill({
  id: 'wf_hundred_heavens', name: 'Hundred Heavens Descent', hue: 0x5fffd0,
  description: 'APEX ART. Leap to a point within 8 m, unleash a twelve-strike flurry, then crash down in a 6 m shockwave. Refills every Chi orb.',
  castType: 'point', cooldown: 300, mana: 0, kind: 'awakening', maxRange: 8, superArmor: 'full', stagger: 'max', weakPoint: 3,
  preStage: stage('Leap', 0.38, [invuln(0, 1.3), move(0, 8, 0.35, { toPoint: true, ignoreCollision: true })]),
  stages: [
    stage('Attack1', 1.3, [
      identityOp(0, 'refill'),
      hits(0.05, 12, 0.07, circle(4), 18, { reaction: FLINCH, stagger: 'high', weakPoint: 0 }),
      hit(0.95, circle(6), 18, { reaction: { kind: 'knockdown', duration: 1.5, power: 1.5 }, vfx: 'heavy' }),
      area(0.95, { shape: circle(6.5), delay: 0, hit: { shape: circle(6.5), coef: 0 }, telegraph: 'none', vfx: 'pillar' }),
    ]),
  ],
});

// Identity skills (Z/X).
const surgeOfChi = skill({
  id: 'wf_surge', name: 'Surge of Chi', hue: 0x5fffd0, kind: 'identity',
  description: 'Available after spending 12 orbs. Instantly refill every Chi orb; your next Chi Art deals 40% more damage. 0.3 s of full immunity.',
  castType: 'normal', cooldown: 1, mana: 0, superArmor: 'full',
  stages: [stage('Buff', 0.3, [invuln(0, 0.3), identityOp(0, 'surge')])],
});

const stillWater = skill({
  id: STANCE, name: 'Still Water', hue: 0x9fe8ff, kind: 'identity',
  description: 'A 0.6 s parry stance. A blow that lands is negated and answered with a counter-strike (250%, counters) that grants 1 orb. If nothing strikes, the cooldown drops to 5 s.',
  castType: 'normal', cooldown: 15, mana: 0,
  // Only a dash can break the stance early.
  stages: [stage('Channel', PARRY_WINDOW, [identityOp(0, 'parry')], { cancelAt: PARRY_WINDOW })],
});

const riposte = skill({
  id: RIPOSTE, name: 'Still Water Riposte', hue: 0x9fe8ff, kind: 'identity',
  description: 'The automatic counter-strike of a successful Still Water parry.',
  castType: 'normal', cooldown: 0, mana: 0, superArmor: 'full', counter: true, stagger: 'mid',
  stages: [stage('Kick', 0.45, [invuln(0, 0.35), hit(0.1, cone(3, 120), 2.5, { reaction: { kind: 'knockback', power: 1.5 }, vfx: 'heavy' })])],
});

// ------------------------------------------------------------------ tripods (facets)

/** Adds a tag read by the identity (`damageMult` / `onSkillCast`), e.g. 'vsStaggered:0.3'. */
const tag = (t: string) => T.custom((s) => {
  s.tags = [...(s.tags ?? []), t];
});
const chiGain = (n: number) => T.custom((s) => {
  s.identityGain = (s.identityGain ?? 0) + n;
});
const hitsOf = (st: SkillStage | undefined): HitSpec[] => (st?.actions ?? []).flatMap((a) => (a.kind === 'hit' ? [a.hit] : []));
const coefOf = (st: SkillStage | undefined): number => hitsOf(st).reduce((sum, h) => sum + h.coef, 0);
const tickHit = (s: ResolvedSkill): HitSpec | undefined => s.holding?.tick.flatMap((a) => (a.kind === 'hit' ? [a.hit] : []))[0];
const radiusOf = (h: HitSpec | undefined, fallback: number): number => (h?.shape.kind === 'circle' ? h.shape.radius : fallback);

/** A lunge at the start of a stage (the strike lands as it ends). */
const lunge = (stageIdx: number, distance: number) => T.custom((s) => {
  const st = s.stages[stageIdx];
  if (st) st.actions = [move(0, distance, 0.14), ...st.actions].sort((a, b) => a.at - b.at);
});

function flashDrop(s: ResolvedSkill): void {
  const total = s.stages.reduce((sum, st) => sum + coefOf(st), 0);
  s.castType = 'point';
  s.maxRange = 10;
  s.preStage = stage('Leap', 0.32, [move(0, 10, 0.3, { toPoint: true, ignoreCollision: true })]);
  s.stages = [stage('Slam', 0.5, [hit(0.06, circle(3), total, { reaction: { kind: 'launch', power: 0.2, duration: 0.7 }, vfx: 'heavy' })])];
}

function whiteCrane(s: ResolvedSkill): void {
  const i = s.stages.length - 1;
  const coef = coefOf(s.stages[i]) * 2.58;
  // The kick flies 6 m; the strike covers the whole flight path behind the landing spot.
  s.stages[i] = stage('Kick', 0.6, [move(0, 6, 0.3, { ignoreCollision: true }), hit(0.3, rect(6.6, 2.6, -6), coef, { reaction: { kind: 'knockdown', duration: 1, power: 1 }, vfx: 'heavy' })]);
}

function fallingPetal(s: ResolvedSkill): void {
  for (const h of hitsOf(s.stages[s.stages.length - 1])) {
    h.coef *= 2.75;
    h.reaction = { kind: 'launch', power: 0.5, duration: 1.1 };
  }
}

function galeFlurry(s: ResolvedSkill): void {
  const st = s.stages[0];
  const h = hitsOf(st)[0];
  if (!st || !h) return;
  h.count = 6;
  h.interval = 0.06;
  st.duration = Math.max(st.duration, 0.6);
}

function axisDive(s: ResolvedSkill): void {
  const spin = hitsOf(s.stages[0])[0];
  const k = radiusOf(spin, 2.5) / 2.5;
  const total = (spin?.coef ?? 9) * 1.59;
  s.castType = 'point';
  s.maxRange = 8;
  s.stages = [
    stage('Leap', 1.25, [
      move(0, 8, 0.35, { toPoint: true, ignoreCollision: true }),
      hits(0.4, 4, 0.12, circle(2.5 * k), total * 0.4, { reaction: FLINCH }),
      hit(0.95, circle(3.5 * k), total * 0.6, { reaction: { kind: 'knockdown', duration: 1, power: 1 }, vfx: 'heavy' }),
    ]),
  ];
}

function endlessGale(s: ResolvedSkill): void {
  const st = s.stages[0];
  const spin = hitsOf(st)[0];
  if (!st || !spin) return;
  spin.count = 28; // the extra second adds 8 kicks
  spin.coef *= 2.43;
  st.duration += 1;
  st.moveMult = (st.moveMult ?? 1) * 1.25;
  st.turnRate = (st.turnRate ?? 8) * 2;
}

function skyTiger(s: ResolvedSkill): void {
  const st = s.stages[0];
  const up = hitsOf(st).find((h) => (h.count ?? 1) === 3);
  if (!st || !up) return;
  up.count = 6;
  up.interval = 0.1;
  up.reaction = { kind: 'launch', power: 0.6, duration: 1.4 };
  st.duration = Math.max(st.duration, 1.3);
}

function doubleCrane(s: ResolvedSkill): void {
  const first = s.stages[0];
  if (!first) return;
  const second = structuredClone(first);
  second.actions = [move(0, 3, 0.15), ...second.actions.map((a): SkillAction => ({ ...a, at: a.at + 0.1 }))];
  second.duration += 0.1;
  s.castType = 'combo';
  s.followWindow = 1.2;
  s.stages = [first, second];
}

function burstStorm(s: ResolvedSkill): void {
  const h = s.holding;
  const tick = tickHit(s);
  if (!h || !tick) return;
  const ticks = Math.round(h.maxHold / h.tickInterval);
  // The whole storm carries the skill's stagger tier (split across its hits).
  const { stagger: _perTick, ...rest } = tick;
  s.castType = 'normal';
  s.holding = undefined;
  s.stages = [stage('Spin', 1.5, [{ at: 0.05, kind: 'hit', hit: { ...rest, coef: tick.coef * ticks, count: ticks, interval: 1.3 / ticks, reaction: FLINCH } }])];
}

function finalGust(s: ResolvedSkill): void {
  const h = s.holding;
  const tick = tickHit(s);
  if (!h || !tick) return;
  h.finisher = stage('Slam', 0.45, [hit(0.1, circle(radiusOf(tick, 3.5) + 1), 0.9 * 13 * 0.95, { reaction: { kind: 'knockdown', duration: 1, power: 1.5 }, vfx: 'heavy', ...(tick.element ? { element: tick.element } : {}) })]);
}

function overdrive(s: ResolvedSkill): void {
  const h = s.holding;
  if (!h) return;
  const ticks = Math.round(h.maxHold / h.tickInterval);
  h.maxHold += 1;
  h.tickInterval = h.maxHold / (ticks + 4);
}

const tripods: ClassDef['tripods'] = {
  wf_thunder_kicks: [
    tripod('wf_kicks_t1a', 1, 'Frugal', 'Mana cost -67%.', T.mp(-0.67)),
    tripod('wf_kicks_t1b', 1, 'Chi Siphon', 'Generates 40 more Chi.', chiGain(40)),
    tripod('wf_kicks_t1c', 1, 'Ruthless', 'Damage +30% against staggered foes.', tag('vsStaggered:0.3')),
    tripod('wf_kicks_t2a', 2, 'Thunder Slam', 'Damage +60% against airborne foes.', tag('vsAirborne:0.6')),
    tripod('wf_kicks_t2b', 2, 'Easy Mark', 'Damage +30% against launched or downed foes.', tag('vsDowned:0.3')),
    tripod('wf_kicks_t2c', 2, 'Static', 'Hits shock foes for 4 s.', T.status({ id: 'shock', duration: 4, value: 0.2 })),
    tripod('wf_kicks_t3a', 3, "Heaven's Crash", 'A rapid five-strike barrage follows the stomp. Damage +95%.', T.addAction(hits(0.62, 5, 0.05, cone(3.5, 90), 3.8 * 0.95, { reaction: FLINCH, weakPoint: 0 }), 0)),
    tripod('wf_kicks_t3b', 3, 'Flash Drop', 'Becomes a point skill: leap up to 10 m and stomp the landing spot. Damage +40%.', T.custom(flashDrop), T.dmg(0.4)),
  ],
  wf_rising_crane: [
    tripod('wf_crane_t1a', 1, 'Long Stride', 'The first kick lunges 4 m forward.', lunge(0, 4)),
    tripod('wf_crane_t1b', 1, 'Swift', 'Attack speed +18%; cooldown +1 s.', T.atkSpeed(0.18), T.cd(1)),
    tripod('wf_crane_t1c', 1, 'Chi Siphon', 'Each strike generates 20 more Chi.', chiGain(60)),
    tripod('wf_crane_t2a', 2, 'Easy Mark', 'Damage +50% against launched foes.', tag('vsAirborne:0.5')),
    tripod('wf_crane_t2b', 2, 'Focus', 'Damage +60%.', T.dmg(0.6)),
    tripod('wf_crane_t2c', 2, 'Brawler', 'Damage +70% against normal and elite foes.', T.vsWeak(0.7)),
    tripod('wf_crane_t3a', 3, 'White Crane', 'The final strike becomes a 6 m flying kick that hits everything along its path. Its damage +158%.', T.custom(whiteCrane)),
    tripod('wf_crane_t3b', 3, 'Falling Petal', 'The final strike launches foes again. Its damage +175%.', T.custom(fallingPetal)),
  ],
  wf_crescent_sweep: [
    tripod('wf_sweep_t1a', 1, 'Long Step', 'The first kick steps 2 m forward.', lunge(0, 2)),
    tripod('wf_sweep_t1b', 1, 'Pressure Point', 'Foes hit take +10% critical hits from your party for 6 s.', T.synergy({ id: 'marked', duration: 6 })),
    tripod('wf_sweep_t1c', 1, 'Swift', 'Attack speed +36%.', T.atkSpeed(0.36)),
    tripod('wf_sweep_t2a', 2, 'Tidal Kick', 'Water element; foes hit are frozen for 2 s. Cooldown +6 s.', T.element('water', { id: 'freeze', duration: 2 }), T.cd(6)),
    tripod('wf_sweep_t2b', 2, 'Wide Sweep', 'Area +20%.', T.aoe(0.2)),
    tripod('wf_sweep_t2c', 2, 'Tailwind', 'Gain +10% attack and move speed for 3 s.', T.addAction(self(0.05, { id: 'wf_tailwind', duration: 3 }), 0)),
    tripod('wf_sweep_t3a', 3, 'Gale Flurry', 'The first kick becomes a six-strike flurry. Damage +205%. No longer counters.', T.custom(galeFlurry), T.dmg(2.05), T.counter(false)),
    tripod('wf_sweep_t3b', 3, 'Deep Breath', 'Generates 80 more Chi.', chiGain(80)),
  ],
  wf_gale_walk: [
    tripod('wf_gale_t1a', 1, 'Storm Heel', 'Lightning element; crit rate +40%.', T.element('lightning'), T.crit(0.4)),
    tripod('wf_gale_t1b', 1, 'Frost Heel', 'Water element; foes hit are slowed by 30% for 2 s.', T.element('water', { id: 'wf_frostbite', duration: 2 })),
    tripod('wf_gale_t1c', 1, 'Ember Heel', 'Fire element; foes hit burn for 3 s.', T.element('fire', { id: 'burn', duration: 3, value: 0.1 })),
    tripod('wf_gale_t2a', 2, 'Wide Spin', 'Area +20%.', T.aoe(0.2)),
    tripod('wf_gale_t2b', 2, 'Quick Prep', 'Cooldown -8 s.', T.cd(-8)),
    tripod('wf_gale_t2c', 2, 'Swift', 'Attack speed +36%.', T.atkSpeed(0.36)),
    tripod('wf_gale_t3a', 3, 'Axis Dive', 'Dive up to 8 m to the cursor, spin four times and slam down. Damage +59%.', T.custom(axisDive)),
    tripod('wf_gale_t3b', 3, 'Endless Gale', 'Lasts 1 s longer (8 more kicks) and steers more easily. Damage +143%.', T.custom(endlessGale)),
  ],
  wf_palm: [
    tripod('wf_palm_t1a', 1, 'Concussion', 'Stagger +1 tier.', T.stagger(1)),
    tripod('wf_palm_t1b', 1, 'Wide Palm', 'Area +20%.', T.aoe(0.2)),
    tripod('wf_palm_t1c', 1, 'Rooted', 'Gain paralysis immunity; the palm draws foes in instead of blasting them away.', T.superArmor('paralysis'), T.reaction({ kind: 'pull', power: 1.5 })),
    tripod('wf_palm_t2a', 2, 'Swift', 'Attack speed +36%.', T.atkSpeed(0.36)),
    tripod('wf_palm_t2b', 2, 'Stilling Palm', 'No knock-back; foes hit are stunned for 3 s.', T.reaction({ kind: 'none' }), T.status({ id: 'stun', duration: 3 })),
    tripod('wf_palm_t2c', 2, 'Tailwind', 'Gain +10% attack and move speed for 4 s.', T.addAction(self(0.05, { id: 'wf_tailwind', duration: 4 }), 0)),
    tripod('wf_palm_t3a', 3, 'Shattering Palm', 'Damage +80%.', T.dmg(0.8)),
    tripod('wf_palm_t3b', 3, 'Bosskiller', 'Damage +65% against push-immune foes.', T.vsBoss(0.65)),
  ],
  wf_tiger: [
    tripod('wf_tiger_t1a', 1, 'Tenacity', 'Gain push immunity.', T.superArmor('push')),
    tripod('wf_tiger_t1b', 1, 'Iron Body', 'Take 40% less damage while casting.', T.damageReduction(0.4)),
    tripod('wf_tiger_t1c', 1, 'Efficient', 'Costs 1 orb. Damage -4%.', T.custom((s) => {
      if (s.resourceCost) s.resourceCost = { ...s.resourceCost, amount: 1 };
    }), T.dmg(-0.04)),
    tripod('wf_tiger_t2a', 2, 'Void Fist', 'Dark element; crit rate +60%.', T.element('dark'), T.crit(0.6)),
    tripod('wf_tiger_t2b', 2, 'Focus', 'Damage +70%.', T.dmg(0.7)),
    tripod('wf_tiger_t2c', 2, 'Fire Ring', 'Leave a burning ring (1.5–4 m) for 6 s.', T.addAction(area(0.7, { shape: ring(1.5, 4), delay: 0, duration: 6, tickInterval: 1, telegraph: 'none', hit: { shape: ring(1.5, 4), coef: 0.4, element: 'fire' } }), 0)),
    tripod('wf_tiger_t3a', 3, 'Sky Tiger', 'Leap higher and strike six times in the air. Damage +130%.', T.custom(skyTiger), T.dmg(1.3)),
    tripod('wf_tiger_t3b', 3, 'Spirit Maw', 'A tiger spirit bites a twice-as-wide area for an extra 140%.', T.addAction(hit(1.0, circle(6), 15 * 1.4, { reaction: { kind: 'knockdown', duration: 1, power: 0.5 }, vfx: 'heavy', stagger: 'none', weakPoint: 0 }), 0)),
  ],
  wf_crane_kick: [
    tripod('wf_tck_t1a', 1, 'Lucky Chi', '50% chance to refund 1 orb.', tag('luckyChi')),
    tripod('wf_tck_t1b', 1, 'Focus', 'Damage +40%.', T.dmg(0.4)),
    tripod('wf_tck_t1c', 1, 'Quick Prep', 'Cooldown -6 s.', T.cd(-6)),
    tripod('wf_tck_t2a', 2, 'Bosskiller', 'Damage +40% against push-immune foes.', T.vsBoss(0.4)),
    tripod('wf_tck_t2b', 2, 'Tenacity', 'Gain push immunity.', T.superArmor('push')),
    tripod('wf_tck_t2c', 2, 'Pierce', 'Ignore 50% of defence.', T.defPen(0.5)),
    tripod('wf_tck_t3a', 3, 'Charged Kick', 'Becomes a 1 s charge skill; releasing in the overcharge window deals +120%.', T.custom((s) => {
      s.castType = 'charge';
      s.charge = { levels: [0.5, 1.0], mults: [0.5, 0.75, 1], overcharge: [1.0, 1.3], overMult: 2.2, anim: 'Charge' };
    })),
    tripod('wf_tck_t3b', 3, 'Double Crane', 'Press again for a free second kick toward the cursor (another 100%).', T.custom(doubleCrane)),
  ],
  wf_cyclone: [
    tripod('wf_cyc_t1a', 1, 'Earth Storm', 'Earth element; damage +30% against normal and elite foes; no longer pulls.', T.element('earth'), T.vsWeak(0.3), T.reaction(FLINCH)),
    tripod('wf_cyc_t1b', 1, 'Frost Storm', 'Water element; ticks chill foes and each has a 20% chance to freeze them for 1.5 s (about every fifth tick).', T.element('water', { id: 'chill', duration: 2 }), T.status({ id: 'freeze', duration: 1.5, chance: 0.2 })),
    tripod('wf_cyc_t1c', 1, 'Lightning Storm', 'Lightning element; each tick has a 20% chance to discharge for an extra 160%.', T.element('lightning'), T.status({ id: 'wf_arc', duration: 0.08, value: 0.9 * 1.6, chance: 0.2 })),
    tripod('wf_cyc_t2a', 2, 'Growing Gale', 'Each tick deals 6% more damage than the last.', tag('growing:0.06')),
    tripod('wf_cyc_t2b', 2, 'Wide Storm', 'Area +30%.', T.aoe(0.3)),
    tripod('wf_cyc_t2c', 2, 'Overdrive', 'Lasts 1 s longer (+4 ticks).', T.custom(overdrive)),
    tripod('wf_cyc_t3a', 3, 'Burst Storm', 'No longer held: a 1.5 s storm of 13 paralysing hits. Damage +70%.', T.custom(burstStorm), T.dmg(0.7)),
    tripod('wf_cyc_t3b', 3, 'Final Gust', 'The storm explodes when it ends for an extra 95%.', T.custom(finalGust)),
  ],
};

// ------------------------------------------------------------------ identity

/** Identity state kept in `identity.data` (numbers only, so it stays serialisable). */
interface ChiState {
  /** Full orbs; Chi Arts pay these through `resourceCost` ('orbs'). */
  orbs: number;
  /** Progress toward the next orb (0..99). */
  chi: number;
  /** Orbs spent toward Surge of Chi (0..12). */
  spent: number;
  /** 1 while Surge's "+40% on the next Chi Art" is waiting. */
  surgeNext: number;
  /** Index + 1 (in CHI_ARTS) of the Chi Art currently empowered by Surge. */
  empowered: number;
  parryOpen: number;
  parryUntil: number;
  parried: number;
}

const chiOf = (a: Actor): ChiState => a.player!.identity.data as unknown as ChiState;

/** Mirrors orbs + partial chi into the gauge the HUD ring shows (0..300). */
function sync(a: Actor): void {
  const d = chiOf(a);
  a.player!.identity.gauge = d.orbs * ORB_CHI + d.chi;
}

export function addChi(a: Actor, amount: number): void {
  const d = chiOf(a);
  if (d.orbs >= MAX_ORBS || amount <= 0) return;
  d.chi += amount;
  while (d.chi >= ORB_CHI && d.orbs < MAX_ORBS) {
    d.chi -= ORB_CHI;
    d.orbs++;
  }
  if (d.orbs >= MAX_ORBS) d.chi = 0;
  sync(a);
}

function addOrbs(a: Actor, n: number): void {
  const d = chiOf(a);
  d.orbs = Math.max(0, Math.min(MAX_ORBS, d.orbs + n));
  if (d.orbs >= MAX_ORBS) d.chi = 0;
  sync(a);
}

/** Damaging hits one full cast lands on a single target (to spread a generator's Chi over its hits). */
function damagingHits(acts: readonly SkillAction[]): number {
  let n = 0;
  for (const x of acts) {
    if (x.kind === 'hit') n += !x.hit.friendly && x.hit.coef > 0 ? (x.hit.count ?? 1) : 0;
    else if (x.kind === 'projectile') n += x.proj.count ?? 1;
    else if (x.kind === 'area' && !x.area.hit.friendly && x.area.hit.coef > 0) {
      const d = x.area.duration ?? 0;
      const ti = x.area.tickInterval ?? 0;
      n += 1 + (d > 0 && ti > 0 ? Math.floor(d / ti) : 0);
    }
  }
  return n;
}

export function hitCount(s: SkillDef): number {
  let n = s.stages.reduce((sum, st) => sum + damagingHits(st.actions), 0);
  if (s.preStage) n += damagingHits(s.preStage.actions);
  if (s.holding) {
    n += damagingHits(s.holding.tick) * Math.max(1, Math.ceil(s.holding.maxHold / s.holding.tickInterval - 1e-6));
    if (s.holding.finisher) n += damagingHits(s.holding.finisher.actions);
  }
  return Math.max(1, n);
}

/** Parry landed: negate, gain an orb and riposte toward the attacker. */
function parry(world: World, a: Actor, res: HitResult): void {
  const d = chiOf(a);
  d.parried = 1;
  addOrbs(a, 1);
  world.emit({ type: 'identity', actorId: a.id, op: 'parry' });
  const s = a.skills.get(RIPOSTE);
  const at = world.actors.get(res.srcId)?.pos ?? res.from;
  // startCast (not useSkill): the riposte must replace the uncancellable stance at once.
  if (s) startCast(world, a, s, { ...at }, { key: 'X' });
}

const identity: IdentityController = {
  gaugeMax: MAX_ORBS * ORB_CHI,
  init(a) {
    const id = a.player!.identity;
    id.gauge = 0;
    id.mode = 'idle';
    id.timer = 0;
    id.data = { orbs: 0, chi: 0, spent: 0, surgeNext: 0, empowered: 0, parryOpen: 0, parryUntil: 0, parried: 0 } satisfies ChiState;
  },
  onSkillCast(world, a, s) {
    const d = chiOf(a);
    const raw = a.player!.identity.data;
    raw[`gain:${s.id}`] = 0;
    // Surge's bonus belongs to one Chi Art cast; any new cast ends it.
    d.empowered = 0;
    if (s.resourceCost?.kind === ORBS) {
      let paid = s.resourceCost.amount;
      if (s.tags?.includes('luckyChi') && world.rng.next() < 0.5) {
        addOrbs(a, 1);
        paid--;
        world.emit({ type: 'identity', actorId: a.id, op: 'luckyChi' });
      }
      d.spent = Math.min(SURGE_COST, d.spent + paid);
      const art = CHI_ARTS.indexOf(s.id) + 1;
      if (d.surgeNext && art > 0) {
        d.surgeNext = 0;
        d.empowered = art;
      }
    }
    sync(a);
  },
  onDamageDealt(world, a, def) {
    if (!def) return;
    // Tripods change the resolved copy (Chi Siphon etc.), so read that one.
    const s: SkillDef & Partial<ResolvedSkill> = a.skills.get(def.id) ?? def;
    const total = (s.identityGain ?? 0) * (s.identityGainMult ?? 1) * a.stats.identityGainMult;
    if (total <= 0) return;
    // Each hit grants its share; extra targets fill faster but a cast never exceeds its listed Chi.
    const raw = a.player!.identity.data;
    const key = `gain:${s.id}`;
    const got = raw[key] ?? 0;
    const add = Math.min(total / hitCount(s), total - got);
    if (add <= 1e-9) return;
    raw[key] = got + add;
    addChi(a, add);
    void world;
  },
  onKey(world, a, key) {
    if (key === 'Z') {
      if (chiOf(a).spent < SURGE_COST) return false;
      const s = a.skills.get('wf_surge');
      return s ? world.useSkill(a, s, 'Z') : false;
    }
    const s = a.skills.get(STANCE);
    return s ? world.useSkill(a, s, 'X') : false;
  },
  op(world, a, op, value) {
    const d = chiOf(a);
    if (op === 'surge') {
      d.orbs = MAX_ORBS;
      d.chi = 0;
      d.spent = Math.max(0, d.spent - SURGE_COST);
      d.surgeNext = 1;
    } else if (op === 'refill') {
      d.orbs = MAX_ORBS;
      d.chi = 0;
    } else if (op === 'chi') {
      addChi(a, value);
    } else if (op === 'parry') {
      d.parryOpen = 1;
      d.parried = 0;
      d.parryUntil = world.time + PARRY_WINDOW;
    }
    sync(a);
  },
  onIncomingHit(world, a, res) {
    const d = chiOf(a);
    if (!d.parryOpen || res.isDot || world.time > d.parryUntil) return false;
    // The first blow triggers the riposte; later blows in the window are still negated.
    if (!d.parried) parry(world, a, res);
    return true;
  },
  update(world, a) {
    const d = chiOf(a);
    if (!d.parryOpen) return;
    const stanceUp = a.cast?.skill.id === STANCE;
    if (world.time <= d.parryUntil && (stanceUp || d.parried)) return;
    d.parryOpen = 0;
    if (!d.parried) {
      // Nothing struck (or the stance was dashed out of): the cooldown is refunded to 5 s.
      if ((a.cooldowns.get(STANCE) ?? 0) > PARRY_REFUND_CD) a.cooldowns.set(STANCE, PARRY_REFUND_CD);
      world.emit({ type: 'identity', actorId: a.id, op: 'parryMiss' });
    }
  },
  damageMult(a, s, target) {
    let m = 1;
    const d = a.player ? chiOf(a) : null;
    if (d?.empowered && CHI_ARTS[d.empowered - 1] === s.id) m *= 1 + SURGE_BONUS;
    for (const t of s.tags ?? []) {
      const [k, v] = t.split(':');
      const x = Number(v);
      if (k === 'vsStaggered' && target?.hasStatus('staggered')) m *= 1 + x;
      else if (k === 'vsAirborne' && target && target.airTimer > 0) m *= 1 + x;
      else if (k === 'vsDowned' && target && (target.airTimer > 0 || target.downTimer > 0)) m *= 1 + x;
      else if (k === 'growing' && s.holding && a.cast?.skill.id === s.id && a.cast.phase === 'holding') m *= 1 + x * Math.floor(a.cast.holdTime / s.holding.tickInterval);
    }
    return m;
  },
  describe(a) {
    const d = chiOf(a);
    const ready = d.spent >= SURGE_COST;
    const label = d.parryOpen ? 'Still Water' : d.surgeNext ? 'Surge +40%' : ready ? 'SURGE' : `Chi ${d.spent}/${SURGE_COST}`;
    return { label, ready, active: !!(d.parryOpen || d.surgeNext || d.empowered), pips: d.orbs, pipsMax: MAX_ORBS };
  },
};

export const WINDFIST: ClassDef = {
  id: 'windfist',
  name: 'Windfist',
  title: 'Gale-Monk Brawler',
  archetype: 'Generator → orb-spender martial artist',
  role: 'dps',
  description: 'A wind-dancing monk in wrapped gauntlets. Generator kicks build Chi Orbs; orb-costing Chi Arts detonate them. Spend enough orbs to Surge, and meet the enemy\'s blows with the perfect stillness of a parry.',
  hue: HUE,
  weapon: 'gauntlet',
  model: 'hero_martial',
  baseStats: (level) => classBaseStats(level, { hp: 1.0, atk: 1.0, speed: 1.04 }),
  skills: [thunderKicks, risingCrane, crescentSweep, galeWalk, palmOfHeaven, tigerAscends, thunderCraneKick, cycloneForm],
  defaultBar: ['wf_thunder_kicks', 'wf_rising_crane', 'wf_crescent_sweep', 'wf_gale_walk', 'wf_palm', 'wf_tiger', 'wf_crane_kick', 'wf_cyclone'],
  basic: { ...basicAttack('wf_basic', 'Windstep Strikes', HUE, { radius: 2.7, angle: 110, coefs: [0.45, 0.45, 0.85] }), description: 'Three-strike palm and kick chain. Costs no mana and generates a little Chi.', identityGain: 24 },
  dash: dashSkill('wf_dash', 'Swift Steps', HUE, 6, 5, { duration: 0.28 }),
  awakenings: [hundredHeavens],
  identitySkills: [surgeOfChi, stillWater, riposte],
  tripods,
  identity,
  identityName: 'Chi',
  identityDescription: 'Generator skills build Chi: 3 orbs of 100. Chi Arts spend orbs instead of mana. After spending 12 orbs press Z for Surge of Chi: every orb refills, you are immune for 0.3 s and your next Chi Art deals +40%. X is Still Water, a 0.6 s parry stance: a blow that lands is negated and answered with a 250% counter-strike (+1 orb); if nothing strikes, its cooldown drops to 5 s.',
};
